// Hook para eliminar plan_ejercicios via RPC eliminar_plan_ejercicio_sin_actividad
// (migration 0015). DELETE conservador con bloqueos defense-in-depth:
//   - TRATAMIENTO_CERRADO si tratamiento no activo.
//   - HAS_SESSIONS si el plan_ejercicio tiene sesiones registradas.
//
// Sprint 3 B4a PR2. La UI usa el code devuelto para branchear el modal
// (confirm → blocked → error) sin abrir múltiples modales.

import { useCallback, useState } from "react";
import { supabase } from "../client";

export type DeleteErrorCode =
  | "NO_AUTH"
  | "NOT_FOUND"
  | "TRATAMIENTO_CERRADO"
  | "HAS_SESSIONS"
  | "UNKNOWN";

export interface DeleteError {
  code: DeleteErrorCode;
  /** Para HAS_SESSIONS: count de sesiones que bloquean el delete. */
  count?: number;
  /** Para UNKNOWN: message raw del RPC. */
  message?: string;
}

export interface UseEliminarPlanEjercicioReturn {
  /** Dispara el RPC. Devuelve true si OK, false si falló (error queda en state). */
  eliminar: (planEjId: string) => Promise<boolean>;
  loading: boolean;
  error: DeleteError | null;
  /** Resetea el state (usado al cerrar el modal). */
  reset: () => void;
}

export function useEliminarPlanEjercicio(): UseEliminarPlanEjercicioReturn {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<DeleteError | null>(null);

  const eliminar = useCallback(async (planEjId: string): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: rpcError } = await supabase.rpc(
        "eliminar_plan_ejercicio_sin_actividad",
        { p_plan_ejercicio_id: planEjId },
      );

      if (rpcError) {
        console.warn("[useEliminarPlanEjercicio] rpc error", rpcError);
        setError({ code: "UNKNOWN", message: rpcError.message });
        return false;
      }

      if (data && typeof data === "object" && data.ok === false) {
        const code = (data.code as DeleteErrorCode | undefined) ?? "UNKNOWN";
        setError({
          code,
          count: typeof data.count === "number" ? data.count : undefined,
          message:
            typeof data.message === "string" ? data.message : undefined,
        });
        return false;
      }

      return true;
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      console.error("[useEliminarPlanEjercicio] throw", e);
      setError({ code: "UNKNOWN", message: msg });
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const reset = useCallback(() => {
    setError(null);
  }, []);

  return { eliminar, loading, error, reset };
}
